import React, { useState } from 'react';
import { Container, Button, ProgressBar, Alert, Row, Col } from 'react-bootstrap';
import RiberaStep1 from './ribera/RiberaStep1';
import RiberaStep2 from './ribera/RiberaStep2';
import RiberaStep3 from './ribera/RiberaStep3';
import RiberaStep4 from './ribera/RiberaStep4';
import RiberaStep5 from './ribera/RiberaStep5';
import RiberaStep6 from './ribera/RiberaStep6';
import './Ribera.css';

const Ribera = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({});
  const [submitted, setSubmitted] = useState(false);

  // Wrapper for safer state updates
  const handleFormDataChange = (newData) => {
    setFormData(prev => {
      // If newData is a function (unlikely but possible from children), handle it
      const nextState = typeof newData === 'function' ? newData(prev) : newData;
      // Merge with previous state to ensure no keys are lost
      return { ...prev, ...nextState };
    });
  };
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [numeroTramite, setNumeroTramite] = useState(null);

  const totalSteps = 6;

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleSubmit = async () => {
    // Prepare FormData for multipart/form-data submission
    const submitData = new FormData();

    // Add text fields
    submitData.append('nombre', formData.nombreApellido?.split(' ')[0] || '');
    submitData.append('apellido', formData.nombreApellido?.split(' ').slice(1).join(' ') || '');
    submitData.append('matricula', formData.matricula || '');
    submitData.append('colegio', formData.colegioProfesional || '');
    submitData.append('email', formData.email || '');
    submitData.append('telefono', formData.telefono || '');

    // Add single files
    if (formData.notaPedidoDeslinde) {
      submitData.append('nota_deslinde', formData.notaPedidoDeslinde);
    }
    if (formData.informeRegistro) {
      submitData.append('informe_registro', formData.informeRegistro);
    }
    if (formData.notaAutorizacion) {
      submitData.append('autorizacion_propietario', formData.notaAutorizacion);
    }
    if (formData.comprobantePago) {
      submitData.append('comprobante_f924', formData.comprobantePago);
    }
    if (formData.planoDXF) {
      submitData.append('plano_dxf', formData.planoDXF);
    }
    if (formData.planoPDF) {
      submitData.append('plano_pdf', formData.planoPDF);
    }
    if (formData.memoriaDescriptiva) {
      submitData.append('memoria_descriptiva', formData.memoriaDescriptiva);
    }

    // Add location data
    if (formData.ubicacionGeografica) {
      submitData.append('ubicacionGeografica', JSON.stringify(formData.ubicacionGeografica));
    }

    // Add multiple files (images)
    // Send images separately as 'imagenes_cauce' and 'imagenes_barranca'
    // Previous strict checks removed in favor of robust checks below


    // Removed GPS images section per Step 4 changes

    // Force array conversion for robustness
    if (formData.imagenesCauce) {
      const files = Array.isArray(formData.imagenesCauce) ? formData.imagenesCauce : [formData.imagenesCauce];
      files.forEach(file => {
        if (file instanceof File) {
          submitData.append('imagenes_cauce', file);
        }
      });
    }

    if (formData.imagenesBarranca) {
      const files = Array.isArray(formData.imagenesBarranca) ? formData.imagenesBarranca : [formData.imagenesBarranca];
      files.forEach(file => {
        if (file instanceof File) {
          submitData.append('imagenes_barranca', file);
        }
      });
    }

    try {
      setSubmitting(true);
      setSubmitError(null);

      const response = await fetch('http://localhost:8000/api/linea-ribera/submit/', {
        method: 'POST',
        body: submitData,
        // Don't set Content-Type header - browser will set it automatically with boundary
      });

      const result = await response.json();

      if (result.success) {
        setNumeroTramite(result.numero_tramite);
        setSubmitted(true);
        window.scrollTo(0, 0);
      } else {
        setSubmitError(result.error || 'Error al enviar la presentación');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitError('Error de conexión con el servidor. Por favor, intente nuevamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setCurrentStep(1);
    setFormData({});
    setSubmitted(false);
    setSubmitting(false);
    setSubmitError(null);
    setNumeroTramite(null);
    window.scrollTo(0, 0);
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <RiberaStep1 formData={formData} onChange={handleFormDataChange} />;
      case 2:
        return <RiberaStep2 formData={formData} onChange={handleFormDataChange} />;
      case 3:
        return <RiberaStep3 formData={formData} onChange={handleFormDataChange} />;
      case 4:
        return <RiberaStep4 formData={formData} onChange={handleFormDataChange} />;
      case 5:
        return <RiberaStep5 formData={formData} onChange={handleFormDataChange} />;
      case 6:
        return <RiberaStep6 formData={formData} onSubmit={handleSubmit} />;
      default:
        return null;
    }
  };

  const getStepTitle = (step) => {
    const titles = {
      1: 'Datos del Profesional',
      2: 'Documentación Administrativa',
      3: 'Documentación Técnica',
      4: 'Información de Campo',
      5: 'Comprobante de Pago',
      6: 'Revisión y Envío'
    };
    return titles[step];
  };

  if (submitted) {
    return (
      <Container className="ribera-container">
        <Alert variant="success" className="text-center shadow-sm p-4">
          <h2 className="mb-3">✅ Presentación Enviada Correctamente</h2>
          <p className="lead mb-4">
            Su presentación de Línea de Ribera ha sido registrada exitosamente.
          </p>
          <div className="mt-4">
            <h5 className="text-primary mb-3">Próximos Pasos:</h5>
            <ol className="text-start" style={{ maxWidth: '600px', margin: '0 auto' }}>
              <li className="mb-2">
                Recibirá un correo electrónico de confirmación de recepcion de su presentación.
              </li>
              <li className="mb-2">
                Mesa de Entradas se comunicará para asignar el número de expediente oficial
              </li>
              <li className="mb-2">
                La documentación será revisada por el área técnica de la DPA
              </li>
              <li className="mb-2">
                En caso de observaciones, se le notificará al email de contacto indicado
              </li>
              <li className="mb-2">
                Una vez aprobada, se emitirá la resolución correspondiente
              </li>
            </ol>
          </div>
          <hr className="my-4" />
          <p className="mb-3">
            <strong>Consultas:</strong> <a href="mailto:estudiosyproyectos@dpatuc.com">estudiosyproyectos@dpatuc.com</a>
          </p>
          <Button variant="primary" size="lg" onClick={handleReset}>
            Iniciar Nueva Presentación
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="ribera-container">
      <div className="ribera-header text-center mb-4">
        <h1 className="mb-2">Presentación Digital - Línea de Ribera</h1>
        <p className="text-muted mb-4">
          Dirección Provincial de Agua - Tucumán
        </p>
      </div>

      <div className="step-progress mb-4">

        <ProgressBar
          now={(currentStep / totalSteps) * 100}
          className="mt-3"
          variant="primary"
          label={`${Math.round((currentStep / totalSteps) * 100)}%`}
        />
      </div>

      {/* Current Step Content */}
      <div className="step-content mb-4">
        {renderStep()}
      </div>

      {/* Error Display */}
      {submitError && (
        <Alert variant="danger" className="mb-4" dismissible onClose={() => setSubmitError(null)}>
          <strong>Error al enviar la presentación:</strong> {submitError}
        </Alert>
      )}

      {/* Navigation Buttons */}
      <Row className="navigation-buttons">
        <Col>
          <Button
            variant="outline-secondary"
            onClick={handlePrevious}
            disabled={currentStep === 1 || submitting}
            size="lg"
          >
            ← Anterior
          </Button>
        </Col>
        <Col className="text-center">
          <span className="text-muted">
            Paso {currentStep} de {totalSteps}
          </span>
        </Col>
        <Col className="text-end">
          {currentStep < totalSteps ? (
            <Button
              variant="primary"
              onClick={handleNext}
              size="lg"
              disabled={submitting}
            >
              Siguiente →
            </Button>
          ) : (
            <Button
              variant="success"
              onClick={handleSubmit}
              size="lg"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Enviando...
                </>
              ) : (
                'Enviar Presentación'
              )}
            </Button>
          )}
        </Col>
      </Row>

      <Alert variant="secondary" className="mt-4 text-center">
        <small>
          <strong>Información:</strong> Para consultas sobre el trámite, contacte a{' '}
          <a href="mailto:mesadeentradas@dpatuc.com">mesadeentradas@dpatuc.com</a>
        </small>
      </Alert>
    </Container>
  );
};

export default Ribera;